import React from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface Project {
  id: number;
  title: string;
  location: string;
  country: string;
  category: string;
  beneficiaries: number;
  progress: number;
  image: string;
  alt: string;
  lat: number;
  lng: number;
  urgency: 'high' | 'medium' | 'low';
}

const ProjectMapSection = () => {
  const projects: Project[] = [
  {
    id: 1,
    title: 'Emergency Food Distribution',
    location: 'Dar es Salaam',
    country: 'Tanzania',
    category: 'Food Security',
    beneficiaries: 12500,
    progress: 78,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b8549cd0-1764661980829.png",
    alt: 'Volunteers distributing food packages to families in outdoor community center',
    lat: -1.2921,
    lng: 36.8219,
    urgency: 'high'
  },
  {
    id: 2,
    title: 'Clean Water Infrastructure',
    location: 'Dhaka',
    country: 'Bangladesh',
    category: 'Water & Sanitation',
    beneficiaries: 8400,
    progress: 62,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_16488d357-1764876891892.png",
    alt: 'Workers installing water pump system in rural village setting',
    lat: 23.8103,
    lng: 90.4125,
    urgency: 'medium'
  },
  {
    id: 3,
    title: 'Medical Clinic Expansion',
    location: 'Port-au-Prince',
    country: 'Haiti',
    category: 'Healthcare',
    beneficiaries: 15200,
    progress: 85,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_10b9b089e-1765294927211.png",
    alt: 'Medical professionals treating patients in newly built clinic facility',
    lat: 18.5944,
    lng: -72.3074,
    urgency: 'high'
  },
  {
    id: 4,
    title: 'Education Support Program',
    location: 'Aleppo',
    country: 'Syria',
    category: 'Education',
    beneficiaries: 6800,
    progress: 54,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1fc531fd9-1767507869733.png",
    alt: 'Children studying at desks in temporary school classroom with teacher',
    lat: 36.2021,
    lng: 37.1343,
    urgency: 'medium'
  },
  {
    id: 5,
    title: 'Refugee Shelter Construction',
    location: 'Cox\'s Bazar',
    country: 'Bangladesh',
    category: 'Shelter',
    beneficiaries: 9600,
    progress: 71,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_157a3f656-1768295280027.png",
    alt: 'Construction workers building temporary shelter structures in refugee camp',
    lat: 21.4272,
    lng: 92.0058,
    urgency: 'high'
  },
  {
    id: 6,
    title: 'Agricultural Recovery Initiative',
    location: 'Addis Ababa',
    country: 'Ethiopia',
    category: 'Livelihood',
    beneficiaries: 11300,
    progress: 68,
    image: "https://images.unsplash.com/photo-1662815123989-d38cafa9d433",
    alt: 'Farmers working in green agricultural field with farming tools and crops',
    lat: 9.0320,
    lng: 38.7469,
    urgency: 'low'
  }];


  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'high':
        return 'bg-error text-error-foreground';
      case 'medium':
        return 'bg-warning text-warning-foreground';
      case 'low':
        return 'bg-success text-success-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-headline font-bold text-foreground mb-4">
            Active Humanitarian Projects
          </h2>
          <p className="text-lg text-muted-foreground font-body max-w-3xl mx-auto">
            Explore our global network of humanitarian initiatives. Each project is tracked in real-time with transparent progress updates and direct impact measurements.
          </p>
        </div>

        <div className="mb-12 bg-card rounded-xl overflow-hidden shadow-lg border border-border">
          <div className="h-96 w-full">
            <iframe
              width="100%"
              height="100%"
              loading="lazy"
              title="Global Humanitarian Projects Map"
              referrerPolicy="no-referrer-when-downgrade"
              src="https://www.google.com/maps?q=-6.369028,34.888822&z=2&output=embed"
              className="border-0" />

          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {projects.map((project) =>
          <div
            key={project.id}
            className="bg-card rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 hover:scale-105 border border-border">

              <div className="relative h-48 overflow-hidden">
                <AppImage
                src={project.image}
                alt={project.alt}
                className="w-full h-full object-cover" />

                <div className="absolute top-4 right-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-cta font-bold ${getUrgencyColor(project.urgency)}`}>
                    {project.urgency.toUpperCase()} PRIORITY
                  </span>
                </div>
              </div>

              <div className="p-6">
                <div className="flex items-center space-x-2 mb-3">
                  <Icon name="MapPinIcon" size={16} className="text-primary" variant="solid" />
                  <span className="text-sm font-body text-muted-foreground">
                    {project.location}, {project.country}
                  </span>
                </div>

                <h3 className="text-xl font-headline font-bold text-foreground mb-2">
                  {project.title}
                </h3>

                <div className="flex items-center space-x-2 mb-4">
                  <span className="px-3 py-1 bg-primary/10 text-primary rounded-full text-xs font-cta font-bold">
                    {project.category}
                  </span>
                </div>

                <div className="space-y-3 mb-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground font-body">Beneficiaries</span>
                    <span className="font-cta font-bold text-foreground">
                      {project.beneficiaries.toLocaleString()}
                    </span>
                  </div>

                  <div>
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="text-muted-foreground font-body">Progress</span>
                      <span className="font-cta font-bold text-primary">{project.progress}%</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
                      <div
                      className="bg-primary h-full rounded-full transition-all duration-500"
                      style={{ width: `${project.progress}%` }} />

                    </div>
                  </div>
                </div>

                <button className="w-full px-4 py-3 bg-primary text-primary-foreground rounded-lg font-cta font-bold text-sm transition-all duration-300 hover:bg-primary/90 flex items-center justify-center space-x-2">
                  <Icon name="EyeIcon" size={18} variant="solid" />
                  <span>View Project Details</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>);

};

export default ProjectMapSection;